//React
import { useEffect, useMemo, useState } from 'react';

// Refine
import { useTranslation } from '@refinedev/core';

//Mantine
import {
  Autocomplete,
  Badge,
  Loader,
  MultiSelect,
  OptionsFilter,
  Select,
  Stack,
  Text,
} from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import { MRT_Cell, MRT_ColumnDef } from 'mantine-react-table';

// Components
import { ProviderSelect } from 'components/ProviderSelect';

import {
  Payor,
  Provider,
  Remittance,
  Report,
  Site,
  Submission,
  User,
} from 'interfaces';
import { useCurrentUserStore } from 'store/UserStore';
// Luxon
import { useListSitesByProvider, useSearchTariffs } from 'api/resource';

// Custom Components
import { useDebouncedValue } from '@mantine/hooks';
import { PatientSearchSelect } from 'components/PatientSearchSelect';
import { useIsProvider, useProviderAllSites } from 'hooks';
import { IconX } from '@tabler/icons-react';
import { getUserFullName } from 'hooks/useGetUsersDisplayInfo';
import { getReportProviderDisplay } from 'utils/reportProviderUtils';
import { useCurrentSiteStore } from 'store';
import { compareDateAsc, formatDateTime, nowISO } from 'utils/dateUtils';

interface ReportColumnProps {
  sites: Site[];
  providers: Provider[];
  submissions: Submission[];
  payors: Payor[];
  remittances?: Remittance[];
  usersByCognitoId?: Record<string, User>;
}

const reportTypeLabels: Record<string, string> = {
  siteReport: 'Claim By Site',
  providerReport: 'Provider Activity',
  statusReport: 'Claim By Status',
  tariffProviderReport: 'Tariff Usage',
  tariffReport: 'Claims By Tariff',
  tariffDateReport: 'Tariff By Date',
  incomeProviderReport: 'Income By Provider',
  incomeSiteReport: 'Income By Site',
  patientReport: 'Patient Report',
  submissionReport: 'Submission Report',
  remittanceReport: 'Remittance Report',
  auditReport: 'Audit Report',
  accountsReceivable: 'Accounts Receivable',
  providerSiteReport: 'Claim By Provider Per Site',
  invoice: 'Invoice',
  providerIncome: 'Provider Income',
  statementIncome: 'Statement Income',
  siteIncome: 'Site Income',
  rejectedBilling: 'Rejected Billing Report',
  delayedBilling: 'Delayed Billing Report',
  customReport: 'Custom Report',
  providermonthly: 'Provider Monthly Report',
  providerytd: 'Provider YTD Report',
  providergrouping: 'Grouping Report',
  providermonthlyRevenueStatement: 'Monthly Revenue Statement',
};

export const ReportColumns = ({
  sites,
  providers,
  submissions,
  payors,
  usersByCognitoId,
}: ReportColumnProps) => {
  const { translate } = useTranslation();
  const localizedReportTypeLabels = {
    ...reportTypeLabels,
    groupIncome: translate(
      'pages:reports.reportTypes.groupIncome',
      'Group Income'
    ),
    sitemonthlyRevenueStatement: translate(
      'pages:reports.reportTypes.siteMonthlyRevenueStatement',
      'Site Monthly Revenue Statement'
    ),
  };
  const isProvider = useIsProvider();
  const { timezone } = useCurrentSiteStore();
  const { isAllSites } = useProviderAllSites();

  const { id, cognitoID, providers: userProviders } = useCurrentUserStore();
  const [selectedProvider, setSelectedProvider] = useState<string>('');

  const { province } = useCurrentSiteStore();

  const columns: MRT_ColumnDef<Report>[] = [
    {
      id: 'type',
      accessorFn: (row) =>
        localizedReportTypeLabels[row.type] ?? 'Not Implemented',
      header: 'Type',
      Cell: ({ cell }) => <Text>{cell.getValue() as string}</Text>,
      Edit: () => <> </>,
      aggregationFn: 'count',
      AggregatedCell: ({ cell }) => {
        return <Text>{cell.getValue() as string}</Text>;
      },
      filterVariant: 'select',
      mantineFilterSelectProps: {
        data: Object.entries(localizedReportTypeLabels)
          .sort((a, b) => a[0].localeCompare(b[0]))
          .map(([key, label]) => ({
            value: key,
            label,
          })),
      },
      enableColumnFilter: true,
    },
    {
      accessorKey: 'userID',
      header: 'User',
      Edit: ({ cell }) => {
        cell.row._valuesCache.userID = id;
        return <></>;
      },
      enableColumnFilter: false,
      visibleInShowHideMenu: false,
    },
    {
      id: 'createdBy',
      accessorFn: (row) => row.cognitoID,
      header: 'Created By',
      Cell: ({ row }) => {
        if (!row.original.cognitoID) {
          return (
            <Text>
              {translate(
                'pages:reports.createdBy.autoGenerated',
                'Auto-generated'
              )}
            </Text>
          );
        }

        const user = usersByCognitoId?.[row.original.cognitoID];
        return (
          <Text>
            {user
              ? getUserFullName(user)
              : translate('pages:reports.createdBy.unknown', 'Unknown')}
          </Text>
        );
      },
      enableColumnFilter: false,
      enableSorting: false,
    },
    {
      accessorKey: 'siteID',
      header: 'Site',
      Cell: ({ cell }) => (
        <Badge color="teal" variant="filled">
          {sites.find((site) => site.id === cell.getValue())?.name}
        </Badge>
      ),
      Edit: ({ cell }) => {
        const { data: providerSites } = useListSitesByProvider({
          providerID: selectedProvider,
        });
        return (
          <SiteSelect
            cell={cell}
            sites={providerSites?.length > 0 ? providerSites : sites}
            hide={true}
          />
        );
      },
      enableColumnFilter: false,
      visibleInShowHideMenu: false,
    },
    {
      accessorKey: 'providerID',
      header: 'Provider',
      Cell: ({ row }) => (
        <Text>
          {getReportProviderDisplay(
            row.original,
            providers,
            translate(
              'pages:reports.customReport.steps.filters.provider.allProviders'
            )
          )}
        </Text>
      ),
      Edit: ({ cell }) => {
        const [selected, setSelected] = useState<string>(
          cell.getValue() as string
        );

        const provider = isProvider
          ? providers.find((p) => p.user?.cognitoID === cognitoID)
          : undefined;

        if (isProvider && provider) {
          cell.row._valuesCache.providerID = provider.id;
        }

        if (isProvider) {
          return (
            <ProviderSelect
              providers={provider ? [provider] : []}
              value={selected || null}
              onChange={(value) => {
                setSelectedProvider(value || null);
              }}
              radius={'lg'}
              w={'60%'}
              disabled
            />
          );
        }
        return (
          <ProviderSelect
            providers={providers}
            value={selected || null}
            onChange={(value) => {
              cell.row._valuesCache.providerID = value;
              setSelected(value || null);
            }}
            radius={'lg'}
            w={'60%'}
          />
        );
      },
      enableColumnFilter: false,
      enableSorting: false,
    },
    {
      accessorKey: 'startDate',
      header: 'Start Date',
      Edit: ({ cell }) => {
        const [value, setValue] = useState<[string | null, string | null]>([
          null,
          null,
        ]);

        const handleDateChange = (dates: [string | null, string | null]) => {
          setValue(dates);
          cell.row._valuesCache.startDate = dates[0];
          cell.row._valuesCache.endDate = dates[1];
        };
        return (
          <DatePickerInput
            type="range"
            label="Date range"
            data-testid={'date-range-picker'}
            placeholder="Date range"
            value={value}
            onChange={handleDateChange}
            radius={'lg'}
            w={'60%'}
          />
        );
      },
      enableColumnFilter: false,
      visibleInShowHideMenu: false,
    },
    {
      accessorKey: 'tariffID',
      header: 'Tariffs',
      Edit: ({ cell }) => {
        const [searchValue, setSearchValue] = useState<string>('');
        const [debounced] = useDebouncedValue(searchValue, 1000);

        const specialtyCode = isProvider ? userProviders[0]?.specialtyCode : '';

        const normalizeSpec = (s: string) => String(Number(s.trim()));

        const {
          data: searchResults = [],
          isLoading,
          isFetching,
        } = useSearchTariffs({
          searchValue: debounced,
          specialtyCode: specialtyCode,
          province,
        });

        const uniqueCombos = useMemo(() => {
          const seen = new Set<string>();
          return searchResults
            .map((t) => {
              const comboKey = `${t.prefix}-${t.code}-${normalizeSpec(
                t.specialtyCode
              )}`;
              return { tariff: t, comboKey };
            })
            .filter(({ comboKey }) => {
              if (seen.has(comboKey)) return false;
              seen.add(comboKey);
              return true;
            })
            .map(({ tariff, comboKey }) => ({
              value: comboKey,
              label: `${tariff.prefix}${tariff.code} — ${
                tariff.description
              } — ${tariff.specialtyCode.trim()}`,
              comboKey,
            }))
            .sort((a, b) =>
              a.label.localeCompare(b.label, undefined, { numeric: true })
            );
        }, [searchResults]);

        const optionsFilter: OptionsFilter = ({ options }) => options;

        const handleOptionSubmit = (comboKey: string) => {
          const [prefix, code, spec] = comboKey.split('-');
          const matchingIds = searchResults
            .filter(
              (t) =>
                String(t.prefix) === prefix &&
                t.code === code &&
                normalizeSpec(t.specialtyCode) === spec
            )
            .map((t) => t.id);
          cell.row._valuesCache.tariffIDs = matchingIds;
          setSearchValue('');
        };

        return (
          <Autocomplete
            placeholder="Search Tariffs"
            data={uniqueCombos}
            value={searchValue}
            onChange={setSearchValue}
            onOptionSubmit={handleOptionSubmit}
            filter={optionsFilter}
            rightSection={
              isLoading || isFetching ? (
                <Loader size={20} style={{ marginRight: 10 }} />
              ) : searchValue ? (
                <IconX
                  size={20}
                  style={{ cursor: 'pointer' }}
                  onClick={() => setSearchValue('')}
                />
              ) : null
            }
            radius="lg"
            style={{ width: '50%' }}
          />
        );
      },
      enableColumnFilter: false,
      visibleInShowHideMenu: false,
    },
    {
      accessorKey: 'patientID',
      header: 'Patient',
      Cell: () => <></>,
      Edit: ({ cell }) => {
        const [selectedValue, setSelectedValue] = useState<string>(
          cell.getValue() as string
        );

        return (
          <PatientSearchSelect
            value={selectedValue || null}
            onChange={(value) => {
              cell.row._valuesCache.patientID = value;
              setSelectedValue(value || '');
            }}
            label="Patient"
            placeholder="Search patients"
            radius="lg"
            w="50%"
            limit={20}
          />
        );
      },
      enableColumnFilter: false,
      visibleInShowHideMenu: false,
    },
    {
      accessorKey: 'submissionID',
      header: 'Submission',
      Cell: () => <></>,
      Edit: ({ cell }) => {
        const [selectedValue, setSelectedValue] = useState<string>(
          cell.getValue() as string
        );

        return (
          <Select
            placeholder="Select Submission"
            data={submissions?.map((submission) => ({
              label: submission?.filename,
              value: submission?.id,
            }))}
            label="Submission"
            value={selectedValue}
            onChange={(value) => {
              cell.row._valuesCache.submissionID = value;
              setSelectedValue(value);
            }}
            radius={'lg'}
            w="50%"
          />
        );
      },
      enableColumnFilter: false,
      visibleInShowHideMenu: false,
    },
    {
      accessorKey: 'headers',
      header: 'Headers',
      Edit: () => <> </>,
      Cell: ({ row }) => {
        return (
          <Stack>
            {row?.original?.headers?.map((header) => (
              <Badge key={header} color="teal" variant="filled">
                {header}
              </Badge>
            ))}
          </Stack>
        );
      },
      enableColumnFilter: false,
      visibleInShowHideMenu: false,
    },
    {
      accessorKey: 'payorID',
      header: 'Payor',
      Edit: ({ cell }) => {
        const [selected, setSelected] = useState<string[]>(
          (cell.getValue() as string[]) || []
        );

        const options = payors.map((p) => ({
          value: p.id,
          label: p.name,
        }));

        return (
          <MultiSelect
            label="Payors"
            placeholder="Select one or more..."
            data={options}
            value={selected}
            multiple
            searchable
            onChange={(vals) => {
              cell.row._valuesCache.payorIDs = vals;
              setSelected(vals);
            }}
            radius="lg"
            w="50%"
          />
        );
      },
      enableColumnFilter: false,
      visibleInShowHideMenu: false,
    },
    {
      accessorKey: 'createdAt',
      accessorFn: (row) => {
        const siteTimezone = sites.find(
          (site) => site.id === row.siteID
        )?.timezone;

        return formatDateTime(row.createdAt, {
          timezone: isAllSites ? siteTimezone : timezone,
        });
      },
      header: 'Created At',
      sortingFn: (rowA, rowB) =>
        compareDateAsc(
          rowA.original.createdAt as string,
          rowB.original.createdAt as string
        ),

      mantineFilterDateInputProps: {
        valueFormat: 'YYYY-MM-DD',
        clearable: true,
        max: nowISO(),
      },
      filterVariant: 'date-range',
      filterFn: 'betweenInclusive',
      enableColumnFilter: true,
    },
  ];
  return columns;
};

const SiteSelect = ({
  cell,
  sites,
  hide = false,
  onValueChange,
}: {
  cell: MRT_Cell<Report>;
  sites: Site[];
  hide: boolean;
  onValueChange?: (value: string | null) => void;
}) => {
  const [selectedValue, setSelectedValue] = useState<string>(
    cell.getValue() as string
  );
  cell.row._valuesCache.siteID = sites[0]?.id;
  useEffect(() => {
    if (sites[0]?.id) {
      onValueChange?.(sites[0].id);
    }
  }, [sites, onValueChange]);
  return sites.length === 1 ? (
    <Select
      placeholder="Select Site"
      data={sites?.map((site) => ({
        label: site.name,
        value: site.id,
      }))}
      label="Site"
      value={sites[0].id}
      onChange={(value) => {
        cell.row._valuesCache.siteID = value;
        setSelectedValue(value);
        onValueChange?.(value ?? null);
      }}
      style={{ visibility: hide ? 'hidden' : null }}
      disabled
      radius={'lg'}
      w={'100%'}
    />
  ) : (
    <Select
      placeholder="Select Site"
      data={sites?.map((site) => ({
        label: site.name,
        value: site.id,
      }))}
      label="Site"
      value={selectedValue}
      onChange={(value) => {
        cell.row._valuesCache.siteID = value;
        setSelectedValue(value);
        onValueChange?.(value ?? null);
      }}
      radius={'lg'}
      w={'100%'}
    />
  );
};
